<template>
    <div
        v-bind:class="{ contained: contained }"
        class="spinner"
        ref="spinnerElement"
    >
        <div class="loading">
            <div class="spinner-elm">Loading&#8230;</div>
            <p v-if="text">{{ text }}</p>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { ref, nextTick, onMounted, onBeforeUnmount } from "vue";
import { ZIndex } from "@primeuix/utils/zindex";
import { usePrimeVue } from "primevue/config";

const props = withDefaults(
    defineProps<{
        contained?: boolean;
        autoZIndex?: boolean;
        text?: string;
    }>(),
    {
        autoZIndex: false,
    }
);

const primevue = usePrimeVue();
const spinnerElement = ref<HTMLElement>();

onMounted(async () => {
    if (props.autoZIndex) {
        await nextTick();
        ZIndex.set(
            "modal",
            spinnerElement.value!,
            primevue.config.zIndex?.modal
        );
    }
});

onBeforeUnmount(() => {
    if (props.autoZIndex) {
        ZIndex.clear(spinnerElement.value!);
    }
});
</script>

<style scoped lang="scss">
/* Absolute Center Spinner */
.spinner {
    --spinner-width: 50px;
    --spinner-height: 50px;

    z-index: 9;
    position: fixed;
    min-height: 100px;
    display: block;
    overflow: visible;
    margin: auto;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    background-color: rgba(255, 255, 255, 0.5);

    .loading {
        display: block;
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);

        /* :not(:required) hides these rules from IE9 and below */
        .spinner-elm:not(:required) {
            /* hide "loading..." text */
            font: 0/0 a;
            position: absolute;
            left: 50%;
            transform: translate(-50%, -50%);
            width: var(--spinner-width);
            height: var(--spinner-height);
            color: transparent;
            text-shadow: none;
            background-color: transparent;
            border: 0;

            &:after {
                content: "";
                display: block;
                font-size: 10px;
                width: 50px;
                height: 50px;
                margin-top: -0.5em;

                border: 10px solid var(--stekz-primary-color);
                border-radius: 100%;
                border-bottom-color: transparent;
                animation: spinner 1s linear 0s infinite;
            }
        }

        p {
            margin-top: calc(var(--spinner-height) / 2);
            position: relative;

            &::after {
                content: "...";
                position: absolute;
                animation: ellipsis 1s infinite linear;
            }
        }
    }

    &.contained {
        position: relative;

        &::after {
            display: table;
            content: "";
            clear: both;
        }

        .loading {
            position: absolute;
        }
    }
}

/* Animations */
@keyframes spinner {
    0% {
        transform: rotate(0deg);
    }
    100% {
        transform: rotate(360deg);
    }
}

@keyframes ellipsis {
    0% {
        content: "";
    }
    25% {
        content: ".";
    }
    50% {
        content: "..";
    }
    75% {
        content: "...";
    }
}
</style>
